import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Button, Card, Typography, Form, Input, Select, DatePicker, Spin, Divider, Space, Upload, Progress, InputNumber, Row, Col } from "antd";
import { ArrowLeftOutlined, SaveOutlined, UploadOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import videoService from "../../../services/videoService";
import masterService from "../../../services/masterService";
import CustomUploader from "../../../components/uploader/CustomUploader";
import ImgCrop from "antd-img-crop";
import dayjs from "dayjs";
import {
  getImageUrl,
  noWhitespaceValidator,
} from "../../../utils/commonFunctions";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EditShow = () => {
  const navigate = useNavigate();
  const { video_show_id } = useParams();
  const notify = useNotify();
  const [form] = Form.useForm();
  const selectedBadge = Form.useWatch("badge", form);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [movieName, setMovieName] = useState("");
  const [genres, setGenres] = useState([]);
  const [subGenres, setSubGenres] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [talents, setTalents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [trailerFiles, setTrailerFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [clipFiles, setClipFiles] = useState([]);
  const [srtFiles, setSrtFiles] = useState([]);
  const [images, setImages] = useState([]);

  const convertOldImageFormatToNew = (oldImages) => {
    if (!Array.isArray(oldImages) || oldImages.length === 0) {
      return [];
    }

    return oldImages.map((oldImage) => {
      const newImage = {
        lang_id: oldImage.lang_id,
        card_image: [],
        banner_image: [],
        horizontal_movie_card_image: [],
        movie_det_banner_image: [],
        movie_logo: [],
      };

      const imageTypes = [
        { oldKey: 'card_image', newKey: 'card_image' },
        { oldKey: 'banner_image', newKey: 'banner_image' },
        { oldKey: 'horizontal_movie_card_image', newKey: 'horizontal_movie_card_image' },
        { oldKey: 'movie_det_banner_image', newKey: 'movie_det_banner_image' },
        { oldKey: 'movie_logo', newKey: 'movie_logo' },
      ];

      imageTypes.forEach(({ oldKey, newKey }) => {
        if (oldImage[oldKey]) {
          if (Array.isArray(oldImage[oldKey])) {
            newImage[newKey] = oldImage[oldKey];
          } else if (typeof oldImage[oldKey] === 'string') {
            newImage[newKey] = [
              {
                image_url: oldImage[oldKey],
                start_date_time: oldImage.start_date_time || null,
                end_date_time: oldImage.end_date_time || null,
              }
            ];
          }
        }
      });

      return newImage;
    });
  };
  const [msCategory, setMsCategory] = useState([]);

  const createEmptyMultilingualEntry = () => ({
    lang_id: null,
    video_show_title: "",
    vs_description: "",
    vs_short_description: "",
    director: "",
    cast_members: "",
    rating_description: "",
    badge_name: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: [],
    seo_image: "",
  });
  const [multilingualData, setMultilingualData] = useState([]);

  const parseDateTimeValue = (value) => {
    if (!value) return null;
    const normalizedValue =
      typeof value === "string" ? value.replace("T", " ").replace("Z", "") : value;
    const parsedValue = dayjs(normalizedValue);
    return parsedValue.isValid() ? parsedValue : null;
  };

  const serializeDateTimeAsIs = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value.format === "function") {
      return value.format("YYYY-MM-DD HH:mm:ss");
    }
    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue.format("YYYY-MM-DD HH:mm:ss") : null;
  };

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        setLoading(true);
        const selectedGenres = form.getFieldValue("genre_list");
        console.log("selectedGenres123", selectedGenres);

        const [
          genresRes,
          subGenresRes,
          ratingsRes,
          languagesRes,
          tagsRes,
          talentsRes,
          countriesRes,
          msCategoryRes
        ] = await Promise.all([
          masterService.getGenres({
            isAll: true,
            status: "Y",
          }),
          masterService.getSubGenres({
            isAll: true,
            genre_id: selectedGenres?.join(",") || "",
          }),
          videoService.getAllVideoRatings({
            perPage: 1000,
            currentPage: 1,
            searchText: "",
            isAll: true,
          }),
          masterService.getVideoLanguages({
            isAll: true,
            status: "Y",
          }),
          masterService.getTagList({
            isAll: true,
            status: "Y",
          }),
          masterService.getTalentList({
            status: "Y",
            isAll: true,
          }),
          masterService.getAllCountries({
            isAll: true,
            status: "Y",
          }),
          masterService.getMasterCategory({
            isAll: true,
            status: "Y",
          })
        ]);
        const pickList = (res) =>
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setGenres(pickList(genresRes));
        setSubGenres(pickList(subGenresRes));
        setRatings(pickList(ratingsRes));
        setLanguages(pickList(languagesRes));
        setTags(pickList(tagsRes));
        setTalents(pickList(talentsRes));
        setMsCategory(pickList(msCategoryRes))
        setCountries(pickList(countriesRes));
      } catch (e) {
        notify.error({ message: "Failed to load form data" });
      } finally {
        setLoading(false);
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    const loadDetails = async () => {
      try {
        setSpinning(true);
        const resp = await videoService.getSingleVideoShow(video_show_id);
        const data = Array.isArray(resp.data?.data)
          ? resp.data.data[0]
          : resp.data?.data;
        if (data) {
          form.setFieldsValue({
            video_show_title: data.video_show_title,
            vs_description: data.vs_description,
            vs_short_description:
              data.vs_short_description || data.short_description || "",
            director: data.director,
            content_type: data.content_type,
            cast_members: data.cast_members,
            rating_description: data.rating_description,
            vs_runtime: data.vs_runtime,
            vs_endtime: data.vs_endtime,
            // vs_original_release_date: data.vs_original_release_date
            //   ? moment(data.vs_original_release_date)
            //   : null,
            // vs_release_date: data.vs_release_date
            //   ? moment(data.vs_release_date)
            //   : null,

            vs_original_release_date: parseDateTimeValue(data.vs_original_release_date),
            vs_release_date: parseDateTimeValue(data.vs_release_date),
            vs_expiry_date: parseDateTimeValue(data.vs_expiry_date),
            talent_list: data.talent_list || [],
            country_of_origin: data.country_of_origin,
            intro_start: data.intro_start || "",
            intro_end: data.intro_end || "",
            outro_start: data.outro_start || "",
            outro_end: data.outro_end || "",
            genre_list: data.genre_list || [],
            category_list: data.mscategory_list || [],
            sub_genre_list: data.sub_genre_list || [],
            lang_list: data.lang_list || [],
            tags_list: data.tags_list || [],
            is_subscription_required: data.is_subscription_required || "N",
            is_downloadable: data.is_downloadable || "N",
            rating_id: data.rating_id,
            trailer_url: data.trailer_url,
            video_show_is_active: data.video_show_is_active || "Y",
            seo_title: data.seo_title || "",
            seo_description: data.seo_description || "",
            seo_image: data.seo_image || "",
            seo_keywords: data.seo_keywords
              ? String(data.seo_keywords)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
            badge: data.badge || "N",
            badge_name: data.badge_name || "",
            countries: Array.isArray(data.allowed_countries)
              ? data.allowed_countries.map((country) =>
                typeof country === 'object' && country.country_id
                  ? country.country_id
                  : country
              )
              : data.allowed_countries
                ? (typeof data.allowed_countries === 'string'
                  ? data.allowed_countries.split(",").map((id) => Number(id.trim())).filter(Boolean)
                  : [data.allowed_countries])
                : [],
          });

          // Set movie name for custom uploader
          setMovieName(data.video_show_title || "");

          // Set file arrays
          setTrailerFiles(data.trailer_files || []);
          setVideoFiles(data.video_files || []);
          setClipFiles(data.clip_files || []);
          setSrtFiles(data.srt_files || []);
          const apiImages = data.images || [];
          const convertedImages = convertOldImageFormatToNew(apiImages);
          setImages(convertedImages);

          setMultilingualData(
            Array.isArray(data.multilingual_data)
              ? data.multilingual_data.map((entry) => ({
                ...createEmptyMultilingualEntry(),
                ...entry,
                seo_keywords: Array.isArray(entry?.seo_keywords)
                  ? entry.seo_keywords
                  : entry?.seo_keywords
                    ? String(entry.seo_keywords)
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                    : [],
              }))
              : []
          );
        }
      } catch (e) {
        notify.error({ message: "Failed to load movie" });
      } finally {
        setSpinning(false);
      }
    };
    if (video_show_id) loadDetails();
  }, [video_show_id]);

  const handleBack = () => {
    navigate("/movies");
  };

  const handleValuesChange = (changedValues) => {
    if (changedValues.video_show_title !== undefined) {
      setMovieName(changedValues.video_show_title);
    }
    if (changedValues.badge === "N") {
      form.setFieldsValue({ badge_name: "" });
      setMultilingualData((prev) =>
        prev.map((entry) => ({ ...entry, badge_name: "" }))
      );
    }
  };

  const getSubGenres = async () => {
    const selectedGenres = form.getFieldValue("genre_list");
    console.log("selectedGenres123", selectedGenres);
    const res = await masterService.getSubGenres({
      isAll: true,
      genre_id: selectedGenres?.join(",") || "",
    });
    console.log("res123", res);
    setSubGenres(res?.data?.data?.data || res?.data?.data || res?.data || []);
  };

  const handleGenreChange = (value) => {
    console.log("value123", value);
    getSubGenres();
    form.setFieldsValue({
      sub_genre_list: [],
    });
  };

  // Time format validator for hh:mm:ss
  const timeFormatValidator = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    if (timeRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Please enter time in hh:mm:ss format (e.g., 00:01:30)'));
  };

  // Header content with user info

  const handleSubmit = async (values) => {
    console.log(handleSubmit, "Here is the submit button clicked")
    // Validate runtime vs end time
    if (
      values.vs_runtime &&
      values.vs_endtime &&
      values.vs_runtime <= values.vs_endtime
    ) {
      notify.error({
        message: "Runtime cannot be less than or equal to end time",
      });
      return;
    }

    // Validate original release date vs release date
    if (values.vs_original_release_date && values.vs_release_date) {
      const originalDate = dayjs(values.vs_original_release_date);
      const releaseDate = dayjs(values.vs_release_date);
      if (originalDate.isAfter(releaseDate)) {
        notify.error({
          message: "Original release date cannot be after release date",
        });
        return;
      }
    }

    if (trailerFiles.length > 0) {
      const invalidTrailer = trailerFiles.some(
        (f) => !f || !f.file_name || !f.bunny_guid
      );
      if (invalidTrailer) {
        notify.error({
          message: "Please upload all added trailer files before submitting",
        });
        return;
      }
    }

    // Video: if entries exist, ensure each has uploaded identifiers
    if (videoFiles.length > 0) {
      const invalidVideo = videoFiles.some(
        (f) => !f || !f.file_name || !f.bunny_guid
      );
      if (invalidVideo) {
        notify.error({
          message: "Please upload all added video files before submitting",
        });
        return;
      }
    }

    // Clip: if entries exist, ensure each has uploaded identifiers
    if (clipFiles.length > 0) {
      const invalidClip = clipFiles.some(
        (f) => !f || !f.file_name || !f.bunny_guid
      );
      if (invalidClip) {
        notify.error({
          message: "Please upload all added clip files before submitting",
        });
        return;
      }
    }

    // Subtitles: if entries exist, ensure each has a URL set (uploaded)
    if (srtFiles.length > 0) {
      const invalidSrt = srtFiles.some((f) => !f || !f.url);
      if (invalidSrt) {
        notify.error({
          message: "Please upload all added subtitle files before submitting",
        });
        return;
      }
    }

    // Images: if entries exist, ensure each has uploaded identifiers
    if (images.length > 0) {
      console.log("images=>", images);
      const invalidImages = images.some(
        (img) => {
          if (!img || !img.lang_id) return true;

          const hasValidCardImages = img.card_image && img.card_image.length > 0 &&
            img.card_image.every(ci => ci.image_url && ci.start_date_time && ci.end_date_time);
          const hasValidBannerImages = img.banner_image && img.banner_image.length > 0 &&
            img.banner_image.every(bi => bi.image_url && bi.start_date_time && bi.end_date_time);
          const hasValidHorizontalImages = img.horizontal_movie_card_image && img.horizontal_movie_card_image.length > 0 &&
            img.horizontal_movie_card_image.every(hi => hi.image_url && hi.start_date_time && hi.end_date_time);
          const hasValidDetBannerImages = img.movie_det_banner_image && img.movie_det_banner_image.length > 0 &&
            img.movie_det_banner_image.every(dbi => dbi.image_url && dbi.start_date_time && dbi.end_date_time);
          const hasValidLogoImages = img.movie_logo && img.movie_logo.length > 0 &&
            img.movie_logo.every(li => li.image_url && li.start_date_time && li.end_date_time);

          return !(hasValidCardImages && hasValidBannerImages && hasValidHorizontalImages &&
            hasValidDetBannerImages && hasValidLogoImages);
        }
      );
      console.log("invalidImages123", invalidImages);
    }

    // Check for duplicate languages in subtitles
    const subtitleLangs = srtFiles.map((file) => file.lang).filter(Boolean);
    if (new Set(subtitleLangs).size !== subtitleLangs.length) {
      notify.error({ message: "Duplicate languages in subtitle files" });
      return;
    }

    // Check for duplicate languages in video files
    const videoLangs = videoFiles.map((file) => file.lang).filter(Boolean);
    if (new Set(videoLangs).size !== videoLangs.length) {
      notify.error({ message: "Duplicate languages in video files" });
      return;
    }

    // Check for duplicate languages in trailer files
    const trailerLangs = trailerFiles.map((file) => file.lang).filter(Boolean);
    if (new Set(trailerLangs).size !== trailerLangs.length) {
      notify.error({ message: "Duplicate languages in trailer files" });
      return;
    }

    // Check for duplicate languages in clip files
    const clipLangs = clipFiles.map((file) => file.lang).filter(Boolean);
    if (new Set(clipLangs).size !== clipLangs.length) {
      notify.error({ message: "Duplicate languages in clip files" });
      return;
    }

    // Check for duplicate languages in images
    const imageLangs = images.map((image) => image.lang_id).filter(Boolean);
    if (new Set(imageLangs).size !== imageLangs.length) {
      notify.error({ message: "Duplicate languages in images" });
      return;
    }

    // Check for duplicate languages in multilingual data
    const multilingualLangs = multilingualData
      .map((data) => data.lang_id)
      .filter(Boolean);
    if (new Set(multilingualLangs).size !== multilingualLangs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }

    // Validate multilingual data completeness
    for (let i = 0; i < multilingualData.length; i++) {
      const data = multilingualData[i];
      if (
        data.lang_id &&
        (!data.video_show_title?.trim() || !data.vs_description?.trim())
      ) {
        notify.error({
          message: `Language entry #${i + 1
            }: Title and description are required when language is selected`,
        });
        return;
      }
    }

    // Validate SEO multilingual data
    const seoEntries = multilingualData.filter((entry) => {
      const hasSeoContent =
        (entry.seo_title && entry.seo_title.trim()) ||
        (entry.seo_description && entry.seo_description.trim()) ||
        (Array.isArray(entry.seo_keywords) && entry.seo_keywords.length > 0);
      return entry.lang_id && hasSeoContent;
    });
    const seoLangs = seoEntries.map((entry) => entry.lang_id);
    if (new Set(seoLangs).size !== seoLangs.length) {
      notify.error({ message: "Duplicate languages in SEO multilingual data" });
      return;
    }
    for (let i = 0; i < seoEntries.length; i++) {
      const entry = seoEntries[i];
      if (!entry.seo_title?.trim() || !entry.seo_description?.trim()) {
        notify.error({
          message: `SEO language entry #${i + 1
            }: Title and description are required when language is selected`,
        });
        return;
      }
    }

    try {
      setSpinning(true);
      const seoKeywords = Array.isArray(values.seo_keywords)
        ? values.seo_keywords
          .map((s) => String(s).trim())
          .filter(Boolean)
          .join(",")
        : values.seo_keywords || "";
      const seoMultilingualSerialized = seoEntries.map((entry) => ({
        lang_id: entry.lang_id,
        seo_title: entry.seo_title?.trim() || "",
        seo_description: entry.seo_description?.trim() || "",
        seo_keywords: Array.isArray(entry.seo_keywords)
          ? entry.seo_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
          : entry.seo_keywords || "",
        seo_image: entry.seo_image || "",
      }));
      const sanitizedMultilingualData = multilingualData.map((entry) => ({
        ...entry,
        badge_name: values.badge === "Y" ? (entry.badge_name || "").trim() : "",
        seo_keywords: Array.isArray(entry.seo_keywords)
          ? entry.seo_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
          : entry.seo_keywords || "",
      }));
      const payload = {
        ...values,
        vs_original_release_date: serializeDateTimeAsIs(
          values.vs_original_release_date
        ),
        vs_release_date: serializeDateTimeAsIs(values.vs_release_date),
        vs_expiry_date: serializeDateTimeAsIs(values.vs_expiry_date),
        genre_id: Array.isArray(values.genre_list)
          ? values.genre_list.join(",")
          : values.genre_list,
        vc_id: Array.isArray(values.category_list)
          ? values.category_list.join(",")
          : values.category_list,
        sub_genre_id: Array.isArray(values.sub_genre_list)
          ? values.sub_genre_list.join(",")
          : values.sub_genre_list,
        trailer_files: trailerFiles,
        video_files: videoFiles,
        clip_files: clipFiles,
        srt_files: srtFiles,
        images: images,
        multilingual_data: sanitizedMultilingualData,
        seo_title: values.seo_title || null,
        seo_description: values.seo_description || null,
        seo_image: values.seo_image || null,
        seo_keywords: seoKeywords,
        badge: values.badge || "N",
        badge_name: values.badge === "Y" ? (values.badge_name || "").trim() : "",
        seo_multilingual_data: seoMultilingualSerialized,
        allowed_countries: Array.isArray(values.countries) && values.countries.length > 0
          ? values.countries.map((countryId) => {
            const country = countries.find((c) => c.country_id === countryId);
            return country
              ? {
                country_code: country.country_code || null,
                country_id: country.country_id,
                country_name: country.country_name || null,
                iso_code: country.iso_code || null,
              }
              : null;
          }).filter(Boolean)
          : [],
      };
      console.log("payload123", payload);
      const resp = await videoService.editVideoShow(video_show_id, payload);
      console.log("resp123", resp);
      if (resp.data?.status) {
        notify.success({ message: "Movie updated successfully" });
        navigate("/movies");
      }
      else {
        notify.error({ message: resp.data?.message || "Failed to update" });
      }
    } catch (e) {
      notify.error({ message: "Failed to update" });
    } finally {
      setSpinning(false);
    }
  };

  const onlyNumber = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 37 &&
      charCode !== 39 &&
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  // Trailer file functions
  const addTrailerFile = () => {
    setTrailerFiles([
      ...trailerFiles,
      {
        lang: null,
        file: null,
        file_name: null,
        percent: 0,
        bunny_guid: null,
        video_id: null,
      },
    ]);
  };

  const handleTrailerFileRemove = (index) => {
    const newFiles = trailerFiles.filter((_, i) => i !== index);
    setTrailerFiles(newFiles);
  };

  const onTrailerUploadComplete = (index, uploadData) => {
    const updated = [...trailerFiles];
    updated[index].file_name = uploadData.file_name;
    updated[index].percent = uploadData.progress;
    updated[index].bunny_guid = uploadData.bunny_guid;
    updated[index].video_id = uploadData.video_id;
    setTrailerFiles(updated);
  };

  // Video file functions
  const addVideoFile = () => {
    setVideoFiles([
      ...videoFiles,
      {
        lang: null,
        file: null,
        file_name: null,
        percent: 0,
        bunny_guid: null,
        video_id: null,
      },
    ]);
  };

  const handleVideoFileRemove = (index) => {
    const newFiles = videoFiles.filter((_, i) => i !== index);
    setVideoFiles(newFiles);
  };

  const onVideoUploadComplete = (index, uploadData) => {
    const updated = [...videoFiles];
    updated[index].file_name = uploadData.file_name;
    updated[index].percent = uploadData.progress;
    updated[index].bunny_guid = uploadData.bunny_guid;
    updated[index].video_id = uploadData.video_id;
    setVideoFiles(updated);
  };

  // Clip file functions
  const addClipFile = () => {
    setClipFiles([
      ...clipFiles,
      {
        lang: null,
        file: null,
        file_name: null,
        percent: 0,
        bunny_guid: null,
        video_id: null,
      },
    ]);
  };

  const handleClipFileRemove = (index) => {
    const newFiles = clipFiles.filter((_, i) => i !== index);
    setClipFiles(newFiles);
  };

  const onClipFileUploadComplete = (index, uploadData) => {
    const updated = [...clipFiles];
    updated[index].file_name = uploadData.file_name;
    updated[index].percent = uploadData.progress;
    updated[index].bunny_guid = uploadData.bunny_guid;
    updated[index].video_id = uploadData.video_id;
    setClipFiles(updated);
  };

  // Subtitle file functions
  const addMoreSubtitles = () => {
    setSrtFiles([
      ...srtFiles,
      { lang: null, file: null, url: null, percent: 0 },
    ]);
  };

  const handleSrtFileRemove = (index) => {
    const newFiles = srtFiles.filter((_, i) => i !== index);
    setSrtFiles(newFiles);
  };

  const onSrtFileUpload = async (file, index) => {
    console.log("File object:", file);
    const formData = new FormData();
    formData.append("subtitle", file); // must be the browser File

    try {
      const response = await videoService.uploadSrtToS3(formData); // change service call if needed
      if (response.data && response.data.status) {
        const newFiles = [...srtFiles];
        newFiles[index].url = response.data.data.s3_key;
        setSrtFiles(newFiles);

        notify.success({ message: "Subtitle uploaded successfully" });
        return false; // Prevent default upload behavior
      } else {
        notify.error({ message: "Failed to upload subtitle file" });
        return false;
      }
    } catch (error) {
      console.error("Error uploading subtitle:", error);
      notify.error({ message: "Error uploading subtitle" });
      return false;
    }
  };

  const addImage = () => {
    setImages([
      ...images,
      {
        lang_id: null,
        card_image: [],
        banner_image: [],
        horizontal_movie_card_image: [],
        movie_det_banner_image: [],
        movie_logo: [],
      },
    ]);
  };

  const addScheduledImage = (imageIndex, imageType) => {
    const newImages = [...images];
    if (!newImages[imageIndex][imageType]) {
      newImages[imageIndex][imageType] = [];
    }
    newImages[imageIndex][imageType].push({
      image_url: null,
      start_date_time: null,
      end_date_time: null,
    });
    setImages(newImages);
  };

  const removeScheduledImage = (imageIndex, imageType, scheduleIndex) => {
    const newImages = [...images];
    newImages[imageIndex][imageType].splice(scheduleIndex, 1);
    setImages(newImages);
  };

  const onScheduledImageUpload = async (file, imageIndex, imageType, scheduleIndex) => {
    if (!file?.type?.startsWith('image/')) {
      notify.error({ message: 'You can only upload image files!' });
      return;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      notify.error({ message: 'Image must be smaller than 10MB!' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "movies");
      formData.append("image", file);

      const response = await masterService.uploadImage(formData);

      if (response?.data?.status && response?.data?.data?.s3_key) {
        const newImages = [...images];
        newImages[imageIndex][imageType][scheduleIndex].image_url = response.data.data.s3_key;
        setImages(newImages);
        notify.success({ message: "Image uploaded successfully" });
      } else {
        notify.error({ message: response?.data?.message || "Failed to upload image" });
      }
    } catch (error) {
      notify.error({
        message: error?.response?.data?.message || "Failed to upload image. Please try again."
      });
    }
  };

  const handleLangImageRemove = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  // Upload handler for SEO Images
  const onSeoImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "movies");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const s3Key = response.data?.data?.s3_key;
      if (s3Key) {
        form.setFieldsValue({ seo_image: s3Key });
        notify.success({ message: "SEO image uploaded successfully" });
      }
    } catch (error) {
      notify.error({ message: "Failed to upload SEO image" });
    }
  };

  // Multilingual data functions
  const addMultilingualData = () => {
    setMultilingualData([...multilingualData, createEmptyMultilingualEntry()]);
  };

  const handleMultilingualDataRemove = (index) => {
    const newMultilingualData = multilingualData.filter((_, i) => i !== index);
    setMultilingualData(newMultilingualData);
  };

  return (
    <div className="">
      <Spin spinning={spinning}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            onFinish={handleSubmit}
            onFinishFailed={({ errorFields }) => {
              const fieldLabels = {
                video_show_title: "Movie Title",
                vs_description: "Description",
                vs_short_description: "Short Description",
                country_of_origin: "Country of Origin",
                genre_list: "Genre",
                category_list: "Category",
                lang_id: "Language",
                rating_id: "Content Rating",
                vs_release_date: "Release Date",
                vs_runtime: "Runtime",
                trailer_files: "Trailer Files",
                video_files: "Video Files",
                clip_files: "Clip Files",
                srt_files: "Subtitle Files",
                images: "Images",
                seo_title: "SEO Title",
                seo_keywords: "SEO Keywords",
                seo_description: "SEO Description",
                vs_type: "Show Type",
                thumbnail_image: "Thumbnail Image",
                poster_image: "Poster Image",
                banner_image: "Banner Image",
                is_subscription_required: "Subscription Required",
              };
              if (errorFields.length > 0) {
                const firstError = errorFields[0];
                const fieldName = firstError.name[0]; // Gets the top-level field name
                const label = fieldLabels[fieldName] ||
                  (typeof fieldName === 'string'
                    ? fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, ' ')
                    : "This field");

                notify.error({ message: `${label} is required` });
              }
            }}
            onValuesChange={handleValuesChange}
          >
            {/* Basic Information Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Basic Information
              </Title>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="video_show_title"
                  label="Movie Title"
                  rules={[
                    { required: true, message: "Please enter movie title" },
                    { validator: noWhitespaceValidator },
                  ]}
                >
                  <Input placeholder="Enter movie title" size="large" />
                </Form.Item>

                <Form.Item name="rating_description" label="Rating Description">
                  <Input placeholder="Enter rating description" size="large" />
                </Form.Item>
              </div>

              <div
                style={{
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="talent_list"
                  label="Talent List"
                >
                  <Select placeholder="Select talent" size="large" mode="multiple"
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {talents.map((talent) => (
                      <Option key={talent.talent_id} value={talent.talent_id}>{talent.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item name="country_of_origin" label="Country of Origin">
                  <Select
                    placeholder="Select country"
                    size="large"
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {countries.map((country) => (
                      <Option key={country.country_id} value={country.country_id}>
                        {country.country_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="badge" label="Badge">
                  <Select placeholder="Select badge option" size="large">
                    <Option value="Y">Yes</Option>
                    <Option value="N">No</Option>
                  </Select>
                </Form.Item>
                
              </div>
              
              {selectedBadge === "Y" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    marginBottom: "24px",
                  }}
                >
                  <Form.Item
                    name="badge_name"
                    label="Badge Name"
                    rules={[
                      { required: true, message: "Please enter badge name" },
                      { max: 20, message: "Badge name cannot exceed 20 characters" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input placeholder="Enter badge name" size="large" maxLength={20} />
                  </Form.Item>
                </div>
              )}
              <Form.Item name="vs_short_description" label="Short Description">
                <TextArea
                  rows={2}
                  placeholder="Enter short description"
                  maxLength={300}
                  showCount
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="vs_description"
                label="Description"
                rules={[
                  { required: true, message: "Please enter description" },
                  { validator: noWhitespaceValidator },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter movie description"
                  maxLength={2000}
                  showCount
                  size="large"
                />
              </Form.Item>

            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Multilingual Data Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Multilingual Data
              </Title>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <Text type="secondary">
                  Add content in different languages for international audiences
                </Text>
                <Button
                  type="dashed"
                  onClick={addMultilingualData}
                  icon={<UploadOutlined />}
                >
                  Add Language
                </Button>
              </div>

              {multilingualData.length === 0 && (
                <Card
                  style={{
                    textAlign: "center",
                    padding: "24px",
                    background: "#fafafa",
                  }}
                >
                  <Text type="secondary">
                    No multilingual data added yet. Click "Add Language" to get
                    started.
                  </Text>
                </Card>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "16px",
                }}
              >
                {multilingualData.map((data, index) => (
                  <Card
                    key={`multilingual-${index}`}
                    style={{ border: "1px solid #d9d9d9" }}
                    title={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Text strong>
                          Language Entry #{index + 1}
                          {data.lang_id &&
                            ` - ${languages.find((l) => l.lang_id === data.lang_id)
                              ?.lang_name || "Unknown Language"
                            }`}
                        </Text>
                        <Button
                          type="link"
                          danger
                          onClick={() => handleMultilingualDataRemove(index)}
                          icon={<DeleteOutlined />}
                        >
                          Remove
                        </Button>
                      </div>
                    }
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                        marginBottom: "16px",
                      }}
                    >
                      <Form.Item
                        label="Language"
                        style={{ marginBottom: 0 }}
                        required
                      >
                        <Select
                          value={data.lang_id}
                          onChange={(value) => {
                            const newData = [...multilingualData];
                            newData[index].lang_id = value;
                            setMultilingualData(newData);
                          }}
                          placeholder="Select language"
                          size="large"
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {languages.map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Movie Title"
                        style={{ marginBottom: 0 }}
                        required
                      >
                        <Input
                          value={data.video_show_title || ""}
                          onChange={(e) => {
                            const newData = [...multilingualData];
                            newData[index].video_show_title = e.target.value;
                            setMultilingualData(newData);
                          }}
                          onBlur={(e) => {
                            const newData = [...multilingualData];
                            newData[index].video_show_title =
                              e.target.value.trim();
                            setMultilingualData(newData);
                          }}
                          placeholder="Enter movie title in this language"
                          size="large"
                        />
                      </Form.Item>
                    </div>

                    <Form.Item
                      label="Short Description"
                      style={{ marginTop: "16px", marginBottom: 0 }}
                    >
                      <TextArea
                        value={data.vs_short_description || ""}
                        onChange={(e) => {
                          const newData = [...multilingualData];
                          newData[index].vs_short_description = e.target.value;
                          setMultilingualData(newData);
                        }}
                        onBlur={(e) => {
                          const newData = [...multilingualData];
                          newData[index].vs_short_description =
                            e.target.value.trim();
                          setMultilingualData(newData);
                        }}
                        rows={2}
                        placeholder="Enter short description in this language"
                        maxLength={300}
                        showCount
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Description"
                      style={{ marginBottom: 0 }}
                      required
                    >
                      <TextArea
                        value={data.vs_description || ""}
                        onChange={(e) => {
                          const newData = [...multilingualData];
                          newData[index].vs_description = e.target.value;
                          setMultilingualData(newData);
                        }}
                        onBlur={(e) => {
                          const newData = [...multilingualData];
                          newData[index].vs_description = e.target.value.trim();
                          setMultilingualData(newData);
                        }}
                        rows={3}
                        placeholder="Enter movie description in this language"
                        maxLength={2000}
                        showCount
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Rating Description"
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        value={data.rating_description || ""}
                        onChange={(e) => {
                          const newData = [...multilingualData];
                          newData[index].rating_description = e.target.value;
                          setMultilingualData(newData);
                        }}
                        onBlur={(e) => {
                          const newData = [...multilingualData];
                          newData[index].rating_description =
                            e.target.value.trim();
                          setMultilingualData(newData);
                        }}
                        placeholder="Enter rating description in this language"
                        size="large"
                      />
                    </Form.Item>

                    {selectedBadge === "Y" && (
                      <Form.Item
                        label="Badge Name"
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          value={data.badge_name || ""}
                          onChange={(e) => {
                            const newData = [...multilingualData];
                            newData[index].badge_name = e.target.value;
                            setMultilingualData(newData);
                          }}
                          onBlur={(e) => {
                            const newData = [...multilingualData];
                            newData[index].badge_name = e.target.value.trim();
                            setMultilingualData(newData);
                          }}
                          placeholder="Enter localized badge name"
                          size="large"
                          maxLength={20}
                        />
                      </Form.Item>
                    )}

                    <Divider style={{ margin: "16px 0" }} />
                    <Title level={5} style={{ marginBottom: "12px" }}>
                      SEO Metadata
                    </Title>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="SEO Title"
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            value={data.seo_title || ""}
                            placeholder="Enter SEO title"
                            size="large"
                            onChange={(e) => {
                              const newData = [...multilingualData];
                              newData[index].seo_title = e.target.value;
                              setMultilingualData(newData);
                            }}
                            onBlur={(e) => {
                              const newData = [...multilingualData];
                              newData[index].seo_title = e.target.value.trim();
                              setMultilingualData(newData);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="SEO Keywords"
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            mode="tags"
                            value={
                              Array.isArray(data.seo_keywords)
                                ? data.seo_keywords
                                : data.seo_keywords
                                  ? String(data.seo_keywords)
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                  : []
                            }
                            placeholder="Type a keyword and press Enter"
                            tokenSeparators={[","]}
                            open={false}
                            size="large"
                            onChange={(value) => {
                              const newData = [...multilingualData];
                              newData[index].seo_keywords = value;
                              setMultilingualData(newData);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label="SEO Description"
                      style={{ marginTop: "16px", marginBottom: 0 }}
                    >
                      <TextArea
                        rows={3}
                        value={data.seo_description || ""}
                        placeholder="Enter SEO description"
                        maxLength={256}
                        showCount
                        size="large"
                        onChange={(e) => {
                          const newData = [...multilingualData];
                          newData[index].seo_description = e.target.value;
                          setMultilingualData(newData);
                        }}
                        onBlur={(e) => {
                          const newData = [...multilingualData];
                          newData[index].seo_description =
                            e.target.value.trim();
                          setMultilingualData(newData);
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      label="SEO Image"
                      style={{ marginTop: "16px", marginBottom: 0 }}
                    >
                      {data.seo_image && (
                        <div style={{ marginBottom: 12 }}>
                          <div
                            style={{
                              padding: "8px 12px",
                              background: "#fff7e6",
                              border: "1px solid #ffd591",
                              borderRadius: 6,
                              color: "#fa8c16",
                              marginBottom: 8,
                            }}
                          >
                            <Text strong>{getImageUrl(data.seo_image)}</Text>
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: "120px",
                              border: "1px solid #d9d9d9",
                              borderRadius: 6,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#fafafa",
                              marginBottom: 8,
                            }}
                          >
                            <img
                              src={getImageUrl(data.seo_image)}
                              alt="SEO preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <ImgCrop aspect={1200 / 630} rotationSlider quality={1}>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={async (file) => {
                            try {
                              const formData = new FormData();
                              formData.append("image_type", "images");
                              formData.append("folder_name", "movies");
                              formData.append("image", file);
                              const response = await masterService.uploadImage(formData);
                              const s3Key = response.data?.data?.s3_key;
                              if (s3Key) {
                                const newData = [...multilingualData];
                                newData[index].seo_image = s3Key;
                                setMultilingualData(newData);
                                notify.success({ message: "SEO image uploaded successfully" });
                              }
                            } catch (error) {
                              notify.error({ message: "Failed to upload SEO image" });
                            }
                            return false;
                          }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: "100%" }}
                          >
                            {data.seo_image ? "Change SEO Image" : "Upload SEO Image"}
                          </Button>
                        </Upload>
                      </ImgCrop>
                    </Form.Item>
                  </Card>
                ))}
              </div>
            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* SEO Configuration Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                {/* SEO Configuration */}
              </Title>

              <Row gutter={[24, 16]} style={{ marginBottom: "16px" }}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="seo_title"
                    label="SEO Title"
                    rules={[{ validator: noWhitespaceValidator }]}
                  >
                    <Input placeholder="Enter SEO title" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item name="seo_keywords" label="SEO Keywords">
                    <Select
                      mode="tags"
                      placeholder="Type a keyword and press Enter"
                      tokenSeparators={[","]}
                      open={false}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="seo_description"
                label="SEO Description"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter SEO description"
                  maxLength={256}
                  showCount
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="seo_image"
                label="SEO Image"
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.seo_image !== currentValues.seo_image
                }
              >
                {({ getFieldValue }) => {
                  const seoImage = getFieldValue("seo_image");
                  return (
                    <>
                      {seoImage && (
                        <div style={{ marginBottom: 12 }}>
                          <div
                            style={{
                              padding: "8px 12px",
                              background: "#fff7e6",
                              border: "1px solid #ffd591",
                              borderRadius: 6,
                              color: "#fa8c16",
                              marginBottom: 8,
                            }}
                          >
                            <Text strong>{getImageUrl(seoImage)}</Text>
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: "120px",
                              border: "1px solid #d9d9d9",
                              borderRadius: 6,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#fafafa",
                              marginBottom: 8,
                            }}
                          >
                            <img
                              src={getImageUrl(seoImage)}
                              alt="SEO preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <ImgCrop aspect={1200 / 630} rotationSlider quality={1}>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={(file) => {
                            onSeoImageUpload(file);
                            return false;
                          }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: "100%" }}
                          >
                            {seoImage ? "Change SEO Image" : "Upload SEO Image"}
                          </Button>
                        </Upload>
                      </ImgCrop>
                    </>
                  );
                }}
              </Form.Item>

              <Divider style={{ margin: "24px 0" }} />

              <Text type="secondary">
                Configure per-language SEO metadata within each entry in the
                Multilingual Data section above.
              </Text>
            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Technical Details Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Technical Details
              </Title>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="vs_runtime"
                  label="Runtime (minutes)"
                  rules={[
                    { required: true, message: "Please enter runtime" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "Runtime must be a non-negative integer",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const endTime = getFieldValue("vs_endtime");
                        if (!value || !endTime) {
                          return Promise.resolve();
                        }
                        if (parseInt(value) <= parseInt(endTime)) {
                          return Promise.reject(
                            new Error("Runtime must be greater than end time")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Enter runtime in minutes"
                    onKeyPress={onlyNumber}
                    size="large"
                  />
                </Form.Item>
                <Form.Item
                  name="vs_endtime"
                  label="End Time (minutes)"
                  rules={[
                    { required: true, message: "Please enter end time" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "End time must be a non-negative integer",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const runtime = getFieldValue("vs_runtime");
                        if (!value || !runtime) {
                          return Promise.resolve();
                        }
                        if (parseInt(value) >= parseInt(runtime)) {
                          return Promise.reject(
                            new Error("End time must be less than runtime")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Enter end time in minutes"
                    onKeyPress={onlyNumber}
                    size="large"
                  />
                </Form.Item>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    marginBottom: "24px",
                  }}
                >
                  <Form.Item
                    name="intro_start"
                    label="Intro Start"
                    rules={[{ validator: timeFormatValidator }]}
                  >
                    <Input
                      placeholder="hh:mm:ss (e.g., 00:01:30)"
                      size="large"
                      maxLength={8}
                    />
                  </Form.Item>
                  <Form.Item
                    name="intro_end"
                    label="Intro End"
                    rules={[{ validator: timeFormatValidator }]}
                  >
                    <Input
                      placeholder="hh:mm:ss (e.g., 00:02:30)"
                      size="large"
                      maxLength={8}
                    />
                  </Form.Item>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    marginBottom: "24px",
                  }}
                >
                  <Form.Item
                    name="outro_start"
                    label="Outro Start"
                    rules={[{ validator: timeFormatValidator }]}
                  >
                    <Input
                      placeholder="hh:mm:ss (e.g., 01:25:00)"
                      size="large"
                      maxLength={8}
                    />
                  </Form.Item>
                  <Form.Item
                    name="outro_end"
                    label="Outro End"
                    rules={[{ validator: timeFormatValidator }]}
                  >
                    <Input
                      placeholder="hh:mm:ss (e.g., 01:26:00)"
                      size="large"
                      maxLength={8}
                    />
                  </Form.Item>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >

                <Form.Item
                  name="vs_original_release_date"
                  label="Original Release Date"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const releaseDate = getFieldValue("vs_release_date");
                        if (!value || !releaseDate) {
                          return Promise.resolve();
                        }
                        if (dayjs(value).isAfter(dayjs(releaseDate))) {
                          return Promise.reject(
                            new Error(
                              "Original release date cannot be after release date"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder="Select original release date"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="vs_release_date"
                  label="Release Date"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const originalDate = getFieldValue(
                          "vs_original_release_date"
                        );
                        if (!value || !originalDate) {
                          return Promise.resolve();
                        }
                        if (dayjs(value).isBefore(dayjs(originalDate))) {
                          return Promise.reject(
                            new Error(
                              "Release date cannot be before original release date"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder="Select release date"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>
                <Form.Item
                  name="vs_expiry_date"
                  label="Expiry Date"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const releaseDate = getFieldValue("vs_release_date");
                        if (!value || !releaseDate) {
                          return Promise.resolve();
                        }
                        if (dayjs(value).isBefore(dayjs(releaseDate))) {
                          return Promise.reject(
                            new Error("Expiry date cannot be before release date")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder="Select expiry date"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>
              </div>
            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Classification Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Classification & Settings
              </Title>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item name="lang_list" label="Show Languages">
                  <Select
                    mode="multiple"
                    placeholder="Select languages"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                  >
                    {languages.map((lang) => (
                      <Option key={lang.lang_id} value={lang.lang_id}>
                        {lang.lang_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="genre_list"
                  label="Movie Genre"
                  rules={[{ required: true, message: "Please select genre" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select genres"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                    onChange={handleGenreChange}
                  >
                    {genres.map((genre) => (
                      <Option key={genre.genre_id} value={genre.genre_id}>
                        {genre.genre_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="sub_genre_list" label="Movie Sub Genre">
                  <Select
                    mode="multiple"
                    placeholder="Select sub genres"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                    disabled={!form.getFieldValue("genre_list")?.length}
                  >
                    {subGenres.map((genre) => (
                      <Option key={genre.genre_id} value={genre.genre_id}>
                        {genre.genre_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="tags_list" label="Tags">
                  <Select
                    mode="multiple"
                    placeholder="Select tags"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                  >
                    {tags.map((tag) => (
                      <Option key={tag.tag_id} value={tag.tag_id}>
                        {tag.tag_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="rating_id"
                  label="Content Rating"
                  rules={[{ required: true, message: "Please select rating" }]}
                >
                  <Select
                    placeholder="Select rating"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                  >
                    {ratings.map((rating) => (
                      <Option key={rating.vr_id} value={rating.vr_id}>
                        {rating.rating_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="category_list"
                  label="Movie Category"
                  rules={[{ required: false, message: "Please select category" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select categories"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"

                  >
                    {msCategory.map((category) => (
                      <Option key={category.vc_id} value={category.vc_id}>
                        {category.vc_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="is_subscription_required"
                  label="Subscription Required"
                  rules={[
                    {
                      required: true,
                      message: "Please select subscription requirement",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select subscription requirement"
                    size="large"
                  >
                    <Option value="Y">Yes</Option>
                    <Option value="N">No</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="is_downloadable"
                  label="Downloadable"
                  rules={[
                    {
                      required: true,
                      message: "Please select downloadable",
                    },
                  ]}
                >
                  <Select placeholder="Select downloadable" size="large">
                    <Option value="Y">Yes</Option>
                    <Option value="N">No</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="content_type" label="Content Type">
                  <Select
                    placeholder="Select content type"
                    size="large"
                    allowClear
                  >
                    <Option value="Originals">Originals</Option>
                    <Option value="Exclusive">Exclusive</Option>
                  </Select>
                </Form.Item>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item name="trailer_url" label="Trailer URL">
                  <Input placeholder="Enter trailer URL" size="large" />
                </Form.Item>
                <Form.Item
                  name="video_show_is_active"
                  label="Status"
                  rules={[{ required: true, message: "Please select status" }]}
                >
                  <Select placeholder="Select status" size="large">
                    <Option value="Y">Active</Option>
                    <Option value="N">Inactive</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="countries" label="Allowed Countries">
                  <Select
                    mode="multiple"
                    placeholder="Select countries"
                    size="large"
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    allowClear
                  >
                    {countries.map((country) => (
                      <Option key={country.country_id} value={country.country_id}>
                        {country.country_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
            
              </div>

            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Media Files Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Media Files
              </Title>

              {/* Trailer Files Subsection */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title
                    level={4}
                    style={{ margin: 0, color: "#52c41a", textAlign: "left" }}
                  >
                    Trailer Files
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addTrailerFile}
                    icon={<UploadOutlined />}
                  >
                    Add Trailer
                  </Button>
                </div>

                {trailerFiles.length === 0 && (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#fafafa",
                    }}
                  >
                    <Text type="secondary">
                      No trailer files added yet. Click "Add Trailer" to get
                      started.
                    </Text>
                  </Card>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {trailerFiles.map((file, index) => (
                    <Card
                      key={`trailer-${index}`}
                      style={{ border: "1px solid #d9d9d9" }}
                    >
                      <Form.Item
                        label="Trailer Language"
                        style={{ marginBottom: "16px" }}
                      >
                        <Select
                          value={file.lang}
                          onChange={(value) => {
                            const newFiles = [...trailerFiles];
                            newFiles[index].lang = value;
                            setTrailerFiles(newFiles);
                          }}
                          placeholder="Select trailer language"
                          size="large"
                          disabled={file.file_name && file.bunny_guid}
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {languages.map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <CustomUploader
                        fileType="trailer"
                        language={
                          file.lang
                            ? languages.find((l) => l.lang_id === file.lang)
                              ?.lang_name
                            : ""
                        }
                        movieName={movieName}
                        onUploadComplete={(uploadData) =>
                          onTrailerUploadComplete(index, uploadData)
                        }
                        onRemove={() => handleTrailerFileRemove(index)}
                        existingFileName={file.file_name}
                        type="movie"
                      />
                    </Card>
                  ))}
                </div>
              </div>

              {/* Video Files Subsection */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title
                    level={4}
                    style={{ margin: 0, color: "#1890ff", textAlign: "left" }}
                  >
                    Video Files
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addVideoFile}
                    icon={<UploadOutlined />}
                  >
                    Add Video
                  </Button>
                </div>

                {videoFiles.length === 0 && (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#fafafa",
                    }}
                  >
                    <Text type="secondary">
                      No video files added yet. Click "Add Video" to get
                      started.
                    </Text>
                  </Card>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {videoFiles.map((file, index) => (
                    <Card
                      key={`video-${index}`}
                      style={{ border: "1px solid #d9d9d9" }}
                    >
                      <Form.Item
                        label="Video Language"
                        style={{ marginBottom: "16px" }}
                      >
                        <Select
                          value={file.lang}
                          onChange={(value) => {
                            const newFiles = [...videoFiles];
                            newFiles[index].lang = value;
                            setVideoFiles(newFiles);
                          }}
                          placeholder="Select video language"
                          size="large"
                          disabled={file.file_name && file.bunny_guid}
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {languages.map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {/* {JSON.stringify(file,null,2)} */}

                      <CustomUploader
                        fileType="movie"
                        language={
                          file.lang
                            ? languages.find((l) => l.lang_id === file.lang)
                              ?.lang_name
                            : ""
                        }
                        movieName={movieName}
                        onUploadComplete={(uploadData) =>
                          onVideoUploadComplete(index, uploadData)
                        }
                        onRemove={() => handleVideoFileRemove(index)}
                        existingFileName={file.file_name}
                        type="movie"
                      />
                    </Card>
                  ))}
                </div>
              </div>

              {/* Clip Files Subsection */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title
                    level={4}
                    style={{ margin: 0, color: "#1890ff", textAlign: "left" }}
                  >
                    Clip Files
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addClipFile}
                    icon={<UploadOutlined />}
                  >
                    Add Clip
                  </Button>
                </div>

                {clipFiles.length === 0 && (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#fafafa",
                    }}
                  >
                    <Text type="secondary">
                      No clip files added yet. Click "Add Clip" to get started.
                    </Text>
                  </Card>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {clipFiles.map((file, index) => (
                    <Card
                      key={`clip-${index}`}
                      style={{ border: "1px solid #d9d9d9" }}
                    >
                      <Form.Item
                        label="Clip Language"
                        style={{ marginBottom: "16px" }}
                      >
                        <Select
                          value={file.lang}
                          onChange={(value) => {
                            const newFiles = [...clipFiles];
                            newFiles[index].lang = value;
                            setClipFiles(newFiles);
                          }}
                          placeholder="Select clip language"
                          size="large"
                          disabled={file.file_name && file.bunny_guid}
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {languages.map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {/* {JSON.stringify(file,null,2)} */}

                      <CustomUploader
                        fileType="clip"
                        language={
                          file.lang
                            ? languages.find((l) => l.lang_id === file.lang)
                              ?.lang_name
                            : ""
                        }
                        movieName={movieName}
                        onUploadComplete={(uploadData) =>
                          onClipFileUploadComplete(index, uploadData)
                        }
                        onRemove={() => handleClipFileRemove(index)}
                        existingFileName={file.file_name}
                        type="movie"
                      />
                    </Card>
                  ))}
                </div>
              </div>

              {/* Subtitle Files Subsection */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title
                    level={4}
                    style={{ margin: 0, color: "#722ed1", textAlign: "left" }}
                  >
                    Subtitle Files
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addMoreSubtitles}
                    icon={<UploadOutlined />}
                  >
                    Add Subtitle
                  </Button>
                </div>

                {srtFiles.length === 0 && (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#fafafa",
                    }}
                  >
                    <Text type="secondary">
                      No subtitle files added yet. Click "Add Subtitle" to get
                      started.
                    </Text>
                  </Card>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {srtFiles.map((file, index) => (
                    <Card
                      key={`srt-${index}`}
                      style={{ border: "1px solid #d9d9d9" }}
                    >
                      <Form.Item
                        label="Subtitle Language"
                        style={{ marginBottom: "16px" }}
                      >
                        <Select
                          value={file.lang}
                          onChange={(value) => {
                            const newFiles = [...srtFiles];
                            newFiles[index].lang = value;
                            setSrtFiles(newFiles);
                          }}
                          placeholder="Select subtitle language"
                          size="large"
                          disabled={file.url}
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {languages.map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Upload Subtitle File"
                        style={{ marginBottom: 0 }}
                      >
                        {file.url && (
                          <div
                            style={{
                              marginBottom: "12px",
                              padding: "8px 12px",
                              background: "#f9f0ff",
                              border: "1px solid #d3adf7",
                              borderRadius: "6px",
                              color: "#722ed1",
                            }}
                          >
                            <Text strong>{file.url.split("/").pop()}</Text>
                          </div>
                        )}
                        <Upload
                          accept=".vtt,.srt"
                          beforeUpload={(file) => {
                            onSrtFileUpload(file, index);
                            return false;
                          }}
                          showUploadList={false}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            size="large"
                            style={{ width: "100%" }}
                            disabled={!file.lang}
                          >
                            Choose Subtitle File
                          </Button>
                        </Upload>
                        <div style={{ marginTop: "12px" }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Supported format: VTT
                          </Text>
                          {file.percent > 0 && (
                            <Progress
                              percent={file.percent}
                              style={{ marginTop: "8px" }}
                            />
                          )}
                          <div
                            style={{ marginTop: "12px", textAlign: "right" }}
                          >
                            <Button
                              type="link"
                              danger
                              onClick={() => handleSrtFileRemove(index)}
                              icon={<DeleteOutlined />}
                            >
                              Remove Subtitle
                            </Button>
                          </div>
                        </div>
                      </Form.Item>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Images Subsection */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title
                    level={4}
                    style={{ margin: 0, color: "#fa8c16", textAlign: "left" }}
                  >
                    Images
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addImage}
                    icon={<UploadOutlined />}
                  >
                    Add Image Set
                  </Button>
                </div>

                {images.length === 0 && (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#fafafa",
                    }}
                  >
                    <Text type="secondary">
                      No image sets added yet. Click "Add Image Set" to get
                      started.
                    </Text>
                  </Card>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  {images.map((image, index) => (
                    <Card
                      key={`image-${index}`}
                      style={{ border: "1px solid #d9d9d9" }}
                    >
                      <Form.Item
                        label="Image Language"
                        style={{ marginBottom: "16px" }}
                      >
                        <Select
                          value={image.lang_id}
                          onChange={(value) => {
                            const newImages = [...images];
                            newImages[index].lang_id = value;
                            setImages(newImages);
                          }}
                          placeholder="Select image language"
                          size="large"
                          disabled={
                            (image.card_image && image.card_image.length > 0) ||
                            (image.banner_image && image.banner_image.length > 0) ||
                            (image.horizontal_movie_card_image && image.horizontal_movie_card_image.length > 0) ||
                            (image.movie_det_banner_image && image.movie_det_banner_image.length > 0) ||
                            (image.movie_logo && image.movie_logo.length > 0)
                          }
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {languages.map((lang) => (
                            <Option key={lang.lang_id} value={lang.lang_id}>
                              {lang.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "16px",
                        }}
                      >

                        <Form.Item
                          label="Horizontal Movie Cards (320 x 180)"
                          style={{ marginBottom: 0 }}
                        >
                          <div style={{ marginBottom: "16px" }}>
                            <Button
                              type="dashed"
                              onClick={() => addScheduledImage(index, "horizontal_movie_card_image")}
                              icon={<PlusOutlined />}
                              disabled={!image.lang_id}
                              style={{ width: "100%", marginBottom: "12px" }}
                            >
                              Add Horizontal Movie Card Image
                            </Button>
                          </div>

                          {image.horizontal_movie_card_image && image.horizontal_movie_card_image.map((scheduledImg, scheduleIndex) => (
                            <Card
                              key={`horizontal_movie_card_image_${scheduleIndex}`}
                              style={{ marginBottom: "16px", border: "1px solid #d9d9d9" }}
                              extra={
                                <Button
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeScheduledImage(index, "horizontal_movie_card_image", scheduleIndex)}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              {scheduledImg.image_url && (
                                <div style={{ marginBottom: "12px" }}>
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fff7e6",
                                      border: "1px solid #ffd591",
                                      borderRadius: 6,
                                      color: "#fa8c16",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Text strong>
                                      {getImageUrl(scheduledImg.image_url)}
                                    </Text>
                                  </div>
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "120px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: 6,
                                      overflow: "hidden",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fafafa",
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(scheduledImg.image_url)}
                                      alt="Horizontal card preview"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                              <ImgCrop
                                aspect={320 / 180}
                                rotationSlider
                                quality={1}
                              >
                                <Upload
                                  accept="image/*"
                                  beforeUpload={async (file) => {
                                    await onScheduledImageUpload(file, index, "horizontal_movie_card_image", scheduleIndex);
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button
                                    icon={<UploadOutlined />}
                                    size="large"
                                    style={{ width: "100%", marginBottom: "12px" }}
                                    disabled={!image.lang_id}
                                  >
                                    {scheduledImg.image_url
                                      ? "Change Horizontal Movie Card Image"
                                      : "Upload Horizontal Movie Card Image"}
                                  </Button>
                                </Upload>
                              </ImgCrop>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <Form.Item label="Start Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select start date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.start_date_time ? dayjs(scheduledImg.start_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].horizontal_movie_card_image[scheduleIndex].start_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item label="End Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select end date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.end_date_time ? dayjs(scheduledImg.end_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].horizontal_movie_card_image[scheduleIndex].end_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </Card>
                          ))}
                        </Form.Item>

                        <Form.Item
                          label="Vertical Movie Cards Image (300x400)"
                          style={{ marginBottom: 0 }}
                        >
                          <div style={{ marginBottom: "16px" }}>
                            <Button
                              type="dashed"
                              onClick={() => addScheduledImage(index, "card_image")}
                              icon={<PlusOutlined />}
                              disabled={!image.lang_id}
                              style={{ width: "100%", marginBottom: "12px" }}
                            >
                              Add Vertical Movie Card Image
                            </Button>
                          </div>

                          {image.card_image && image.card_image.map((scheduledImg, scheduleIndex) => (
                            <Card
                              key={`card_image_${scheduleIndex}`}
                              style={{ marginBottom: "16px", border: "1px solid #d9d9d9" }}
                              extra={
                                <Button
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeScheduledImage(index, "card_image", scheduleIndex)}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              {scheduledImg.image_url && (
                                <div style={{ marginBottom: "12px" }}>
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fff7e6",
                                      border: "1px solid #ffd591",
                                      borderRadius: "6px",
                                      color: "#fa8c16",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Text strong>
                                      {getImageUrl(scheduledImg.image_url)}
                                    </Text>
                                  </div>
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "120px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: "6px",
                                      overflow: "hidden",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fafafa",
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(scheduledImg.image_url)}
                                      alt="Card preview"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                              <ImgCrop
                                aspect={300 / 400}
                                rotationSlider
                                quality={1}
                              >
                                <Upload
                                  accept="image/*"
                                  beforeUpload={async (file) => {
                                    await onScheduledImageUpload(file, index, "card_image", scheduleIndex);
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button
                                    icon={<UploadOutlined />}
                                    size="large"
                                    style={{ width: "100%", marginBottom: "12px" }}
                                    disabled={!image.lang_id}
                                  >
                                    {scheduledImg.image_url
                                      ? "Change Vertical Movie Card Image"
                                      : "Upload Vertical Movie Card Image"}
                                  </Button>
                                </Upload>
                              </ImgCrop>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <Form.Item label="Start Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select start date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.start_date_time ? dayjs(scheduledImg.start_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].card_image[scheduleIndex].start_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item label="End Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select end date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.end_date_time ? dayjs(scheduledImg.end_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].card_image[scheduleIndex].end_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </Card>
                          ))}
                        </Form.Item>

                        <Form.Item
                          label="Sleek Banners Image (1280x720)"
                          style={{ marginBottom: 0 }}
                        >
                          <div style={{ marginBottom: "16px" }}>
                            <Button
                              type="dashed"
                              onClick={() => addScheduledImage(index, "banner_image")}
                              icon={<PlusOutlined />}
                              disabled={!image.lang_id}
                              style={{ width: "100%", marginBottom: "12px" }}
                            >
                              Add Sleek Banner Image
                            </Button>
                          </div>

                          {image.banner_image && image.banner_image.map((scheduledImg, scheduleIndex) => (
                            <Card
                              key={`banner_image_${scheduleIndex}`}
                              style={{ marginBottom: "16px", border: "1px solid #d9d9d9" }}
                              extra={
                                <Button
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeScheduledImage(index, "banner_image", scheduleIndex)}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              {scheduledImg.image_url && (
                                <div style={{ marginBottom: "12px" }}>
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fff7e6",
                                      border: "1px solid #ffd591",
                                      borderRadius: "6px",
                                      color: "#fa8c16",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Text strong>
                                      {getImageUrl(scheduledImg.image_url)}
                                    </Text>
                                  </div>
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "120px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: "6px",
                                      overflow: "hidden",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fafafa",
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(scheduledImg.image_url)}
                                      alt="Banner preview"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                              <ImgCrop
                                aspect={1280 / 720}
                                rotationSlider
                                quality={0.9}
                              >
                                <Upload
                                  accept="image/*"
                                  beforeUpload={async (file) => {
                                    await onScheduledImageUpload(file, index, "banner_image", scheduleIndex);
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button
                                    icon={<UploadOutlined />}
                                    size="large"
                                    style={{ width: "100%", marginBottom: "12px" }}
                                    disabled={!image.lang_id}
                                  >
                                    {scheduledImg.image_url
                                      ? "Change Sleek Banner Image"
                                      : "Upload Sleek Banner Image"}
                                  </Button>
                                </Upload>
                              </ImgCrop>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <Form.Item label="Start Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select start date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.start_date_time ? dayjs(scheduledImg.start_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].banner_image[scheduleIndex].start_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item label="End Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select end date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.end_date_time ? dayjs(scheduledImg.end_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].banner_image[scheduleIndex].end_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </Card>
                          ))}
                        </Form.Item>

                        <Form.Item
                          label="Movie Detail Banner (400x225)"
                          style={{ marginBottom: 0 }}
                        >
                          <div style={{ marginBottom: "16px" }}>
                            <Button
                              type="dashed"
                              onClick={() => addScheduledImage(index, "movie_det_banner_image")}
                              icon={<PlusOutlined />}
                              disabled={!image.lang_id}
                              style={{ width: "100%", marginBottom: "12px" }}
                            >
                              Add Movie Detail Banner Image
                            </Button>
                          </div>

                          {image.movie_det_banner_image && image.movie_det_banner_image.map((scheduledImg, scheduleIndex) => (
                            <Card
                              key={`movie_det_banner_image_${scheduleIndex}`}
                              style={{ marginBottom: "16px", border: "1px solid #d9d9d9" }}
                              extra={
                                <Button
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeScheduledImage(index, "movie_det_banner_image", scheduleIndex)}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              {scheduledImg.image_url && (
                                <div style={{ marginBottom: "12px" }}>
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fff7e6",
                                      border: "1px solid #ffd591",
                                      borderRadius: 6,
                                      color: "#fa8c16",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Text strong>
                                      {getImageUrl(scheduledImg.image_url)}
                                    </Text>
                                  </div>
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "120px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: 6,
                                      overflow: "hidden",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fafafa",
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(scheduledImg.image_url)}
                                      alt="Banner preview"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                              <ImgCrop
                                aspect={400 / 225}
                                rotationSlider
                                quality={1}
                              >
                                <Upload
                                  accept="image/*"
                                  beforeUpload={async (file) => {
                                    await onScheduledImageUpload(file, index, "movie_det_banner_image", scheduleIndex);
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button
                                    icon={<UploadOutlined />}
                                    size="large"
                                    style={{ width: "100%", marginBottom: "12px" }}
                                    disabled={!image.lang_id}
                                  >
                                    {scheduledImg.image_url
                                      ? "Change Movie Detail Banner Image"
                                      : "Upload Movie Detail Banner Image"}
                                  </Button>
                                </Upload>
                              </ImgCrop>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <Form.Item label="Start Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select start date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.start_date_time ? dayjs(scheduledImg.start_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].movie_det_banner_image[scheduleIndex].start_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item label="End Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select end date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.end_date_time ? dayjs(scheduledImg.end_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].movie_det_banner_image[scheduleIndex].end_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </Card>
                          ))}
                        </Form.Item>
                        <Form.Item
                          label="Movie Logo"
                          style={{ marginBottom: 0 }}
                        >
                          <div style={{ marginBottom: "16px" }}>
                            <Button
                              type="dashed"
                              onClick={() => addScheduledImage(index, "movie_logo")}
                              icon={<PlusOutlined />}
                              disabled={!image.lang_id}
                              style={{ width: "100%", marginBottom: "12px" }}
                            >
                              Add Movie Logo
                            </Button>
                          </div>

                          {image.movie_logo && image.movie_logo.map((scheduledImg, scheduleIndex) => (
                            <Card
                              key={`movie_logo_${scheduleIndex}`}
                              style={{ marginBottom: "16px", border: "1px solid #d9d9d9" }}
                              extra={
                                <Button
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeScheduledImage(index, "movie_logo", scheduleIndex)}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              {scheduledImg.image_url && (
                                <div style={{ marginBottom: "12px" }}>
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fff7e6",
                                      border: "1px solid #ffd591",
                                      borderRadius: 6,
                                      color: "#fa8c16",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Text strong>
                                      {getImageUrl(scheduledImg.image_url)}
                                    </Text>
                                  </div>
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "120px",
                                      border: "1px solid #d9d9d9",
                                      borderRadius: 6,
                                      overflow: "hidden",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fafafa",
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(scheduledImg.image_url)}
                                      alt="Movie logo preview"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                              <Upload
                                accept="image/*"
                                beforeUpload={async (file) => {
                                  await onScheduledImageUpload(file, index, "movie_logo", scheduleIndex);
                                  return false;
                                }}
                                showUploadList={false}
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  size="large"
                                  style={{ width: "100%", marginBottom: "12px" }}
                                  disabled={!image.lang_id}
                                >
                                  {scheduledImg.image_url
                                    ? "Change Movie Logo"
                                    : "Upload Movie Logo"}
                                </Button>
                              </Upload>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <Form.Item label="Start Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select start date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.start_date_time ? dayjs(scheduledImg.start_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].movie_logo[scheduleIndex].start_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item label="End Date & Time">
                                  <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select end date and time"
                                    style={{ width: "100%" }}
                                    size="large"
                                    value={scheduledImg.end_date_time ? dayjs(scheduledImg.end_date_time) : null}
                                    onChange={(date) => {
                                      const newImages = [...images];
                                      newImages[index].movie_logo[scheduleIndex].end_date_time = date
                                        ? date.format("YYYY-MM-DD HH:mm:ss")
                                        : null;
                                      setImages(newImages);
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </Card>
                          ))}
                        </Form.Item>
                      </div>

                      <div style={{ marginTop: "16px", textAlign: "right" }}>
                        <Button
                          type="link"
                          danger
                          onClick={() => handleLangImageRemove(index)}
                          icon={<DeleteOutlined />}
                        >
                          Remove Image Set
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Form Actions */}
            <div
              style={{
                background: "#fafafa",
                padding: "24px",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
                textAlign: "center",
              }}
            >
              <Space size="large">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={spinning}
                  size="large"
                  style={{ minWidth: "160px" }}
                >
                  Save Changes
                </Button>
                <Button
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  style={{ minWidth: "120px" }}
                >
                  Go Back
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditShow;
